import { useConfig } from '@marine/configs';
import { useCallback } from 'react';
import { useApiServices } from '../../providers';

export type ImageUrlFormats = {
  base?: string;
  card?: string;
  cardLg?: string;
  cardSm?: string;
  thumbnail?: string;
  thumbnailLg?: string;
  thumbnailSm?: string;
  cover?: string;
  coverSm?: string;
};

export type ImageFormat = keyof ImageUrlFormats;

/** The Clerk JWT template the file service accepts on `?token=`. Lives an hour. */
export const IMAGE_TOKEN_TEMPLATE = 'one_hour_token';

/**
 * An image ready to hand to `expo-image`'s `source`.
 *
 * Structurally typed rather than imported from `expo-image`, because this package is shared with the
 * web apps and must not depend on it. `cacheKey` is the point of the type: the signed `uri` carries a
 * Clerk token that rotates, and every image cache — expo-image included — keys off the URI, so a new
 * token would otherwise re-download and re-decode a picture the device already has. The key is built
 * from the file id and format instead, which is what actually identifies the bytes.
 */
export type ImageSource = {
  uri: string;
  cacheKey: string;
};

export type ImageSources = {
  [K in ImageFormat]?: ImageSource;
};

/** The `format` query the file service renders. `base` is the original, so it has none. */
const IMAGE_FORMAT_PARAMS: Record<ImageFormat, string | null> = {
  base: null,
  card: 'card',
  cardLg: 'card-lg',
  cardSm: 'card-sm',
  thumbnail: 'thumbnail',
  thumbnailLg: 'thumbnail-lg',
  thumbnailSm: 'thumbnail-sm',
  cover: 'cover',
  coverSm: 'cover-sm',
};

const IMAGE_FORMATS = Object.keys(IMAGE_FORMAT_PARAMS) as ImageFormat[];

/** Every format of one already-signed URL. The token is a query param, so `format` joins with `&`. */
export const buildImageUrlFormats = (signedUrl: string): ImageUrlFormats =>
  IMAGE_FORMATS.reduce<ImageUrlFormats>((urls, format) => {
    const param = IMAGE_FORMAT_PARAMS[format];
    urls[format] = param ? `${signedUrl}&format=${param}` : signedUrl;
    return urls;
  }, {});

export const buildFileUrlFromId = (apiHost: string, fileId: string | number): string =>
  `${apiHost}/web-api/files/${fileId}/placeholder.jpg`;

export const signFileUrl = (fileUrl: string, token: string): string =>
  `${fileUrl.replace(/^http:/, 'https:')}?token=${token}`;

/**
 * The same URLs paired with a token-free cache key, so a token refresh doesn't invalidate a picture
 * the device has already downloaded and decoded.
 */
export const buildImageSources = (urls: ImageUrlFormats, fileId: string | number): ImageSources =>
  IMAGE_FORMATS.reduce<ImageSources>((sources, format) => {
    const uri = urls[format];
    if (uri) {
      sources[format] = { uri, cacheKey: `mdc-file-${fileId}-${format}` };
    }
    return sources;
  }, {});

export const useFileUrlWithClerkToken = () => {
  const { mdcApiHost } = useConfig();
  const { getToken } = useApiServices();

  // http://beta-api.marinedatacloud.com/web-api/files/86/pexels-ryan-west-1719648.jpg?format=card

  const attacheClerkTokenToUrl = useCallback(
    async (fileUrl: string): Promise<string> => {
      const accessToken = await getToken({ template: IMAGE_TOKEN_TEMPLATE });

      return signFileUrl(fileUrl, accessToken ?? '');
    },
    [getToken],
  );

  const getFileUrlWithTokenFromId = useCallback(
    async (fileId: string | number): Promise<string> => {
      const url = await attacheClerkTokenToUrl(buildFileUrlFromId(mdcApiHost, fileId));

      return url;
    },
    [attacheClerkTokenToUrl, mdcApiHost],
  );

  const getFileUrlWithTokenFromUrl = useCallback(
    async (fileUrl: string): Promise<string> => {
      const url = await attacheClerkTokenToUrl(fileUrl);

      return url;
    },
    [attacheClerkTokenToUrl],
  );

  const getImageUrlsWithTokenFromId = useCallback(
    async (fileId: string | number): Promise<ImageUrlFormats> => {
      const url = await getFileUrlWithTokenFromId(fileId);

      return buildImageUrlFormats(url);
    },
    [getFileUrlWithTokenFromId],
  );

  const getImageUrlsWithTokenFromUrl = useCallback(
    async (fileUrl?: string): Promise<ImageUrlFormats> => {
      if (!fileUrl) {
        return {};
      }
      const url = await getFileUrlWithTokenFromUrl(fileUrl);

      return buildImageUrlFormats(url);
    },
    [getFileUrlWithTokenFromUrl],
  );

  return {
    getFileUrlWithTokenFromId,
    getFileUrlWithTokenFromUrl,
    getImageUrlsWithTokenFromId,
    getImageUrlsWithTokenFromUrl,
  };
};
